
import { useRef, useState } from "react";
import { Camera, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ProfileHeaderProps {
  coverPhoto?: string;
  profilePic?: string;
  onUpload: (url: string, type: 'profile' | 'cover') => Promise<void>;
}

export const ProfileHeader = ({ coverPhoto, profilePic, onUpload }: ProfileHeaderProps) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState<'profile' | 'cover' | null>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const profileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, type: 'profile' | 'cover') => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setUploading(type);
      
      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${type === 'profile' ? 'profile-pics' : 'cover-photos'}/${fileName}`;
      
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to upload images.",
          variant: "destructive",
        });
        setUploading(null);
        return;
      }
      
      console.log(`Uploading ${type} photo to path: ${filePath}`);
      console.log('File details:', { name: file.name, size: file.size, type: file.type });
      
      // Upload the file
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      console.log('Upload successful:', uploadData);

      // Get the public URL
      const { data } = supabase.storage.from('images').getPublicUrl(filePath);
      
      console.log('Public URL:', data?.publicUrl);
      
      if (data?.publicUrl) {
        console.log(`Calling onUpload with URL: ${data.publicUrl} and type: ${type}`);
        await onUpload(data.publicUrl, type);
        toast({
          title: "Success",
          description: `${type === 'profile' ? 'Profile' : 'Cover'} photo uploaded successfully.`,
        });
      } else {
        console.error('No public URL returned');
        throw new Error('Failed to get public URL for uploaded image');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "There was an error uploading your image.",
        variant: "destructive",
      });
    } finally {
      setUploading(null);
      // Reset the file input
      if (type === 'profile' && profileInputRef.current) {
        profileInputRef.current.value = '';
      } else if (type === 'cover' && coverInputRef.current) {
        coverInputRef.current.value = '';
      }
    }
  };

  const triggerFileInput = (type: 'profile' | 'cover') => {
    console.log(`Triggering file input for ${type}`);
    if (type === 'profile' && profileInputRef.current) {
      profileInputRef.current.click();
    } else if (type === 'cover' && coverInputRef.current) {
      coverInputRef.current.click();
    }
  };

  return (
    <>
      <div className="relative h-48 bg-gradient-to-r from-accent/10 to-primary/10">
        {coverPhoto ? (
          <div className="relative w-full h-full">
            <img
              src={coverPhoto}
              alt="Cover"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-2 right-2">
              <Button 
                size="sm" 
                variant="secondary" 
                className="opacity-70 hover:opacity-100"
                onClick={() => triggerFileInput('cover')}
                disabled={!!uploading}
              >
                <Upload className="w-3 h-3 mr-1" />
                Change
              </Button>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Button 
              variant="ghost" 
              className="gap-2"
              onClick={() => triggerFileInput('cover')}
              disabled={!!uploading}
              type="button" 
            >
              <Upload className="w-4 h-4" />
              {uploading === 'cover' ? 'Uploading...' : 'Upload Cover Photo'}
            </Button>
          </div>
        )}
        <input
          type="file"
          ref={coverInputRef}
          onChange={(e) => handleFileUpload(e, 'cover')}
          accept="image/*"
          className="hidden"
        />
      </div>

      <div className="container mx-auto px-4 -mt-16 relative z-10">
        <div className="relative w-32 h-32 rounded-full border-4 border-background overflow-hidden bg-muted">
          {profilePic ? (
            <div className="relative w-full h-full group">
              <img
                src={profilePic}
                alt="Profile"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                   onClick={() => triggerFileInput('profile')}>
                <Upload className="w-5 h-5 text-white" />
              </div>
            </div>
          ) : (
            <div 
              className="w-full h-full flex items-center justify-center cursor-pointer hover:bg-muted-foreground/10 transition-colors"
              onClick={() => triggerFileInput('profile')}
            >
              {uploading === 'profile' ? (
                <div className="text-xs text-center text-muted-foreground">Uploading...</div>
              ) : (
                <Camera className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
          )}
          <input
            type="file"
            ref={profileInputRef}
            onChange={(e) => handleFileUpload(e, 'profile')}
            accept="image/*"
            className="hidden"
          />
        </div>
      </div>
    </>
  );
};
